import { motion } from "framer-motion";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import React, { useRef } from "react";
import Images from "../../Images";

const validationSchema = Yup.object({
  otp_one: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_two: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_three: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_four: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
});

const initialValues = {
  otp_one: "",
  otp_two: "",
  otp_three: "",
  otp_four: "",
};

function VerifyOtp() {
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleKeyUp = (e, fieldIndex, setFieldValue) => {
    const value = e.target.value;

    if (value.match(/^[0-9]$/)) {
      setFieldValue(`otp_${fieldIndex + 1}`, value);
      if (fieldIndex < 3) inputRefs[fieldIndex + 1].current.focus();
    } else if (e.key === "Backspace") {
      setFieldValue(`otp_${fieldIndex + 1}`, "");
      if (fieldIndex > 0) inputRefs[fieldIndex - 1].current.focus();
    }
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="absolute z-20 p-4 shadow-lg top-10 right-10 bg-white/30 backdrop-blur-md rounded-2xl"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Vendor Account Security</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute z-20 p-4 shadow-lg bottom-20 left-10 bg-white/30 backdrop-blur-md rounded-2xl"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Business Verification</span>
          </div>
        </motion.div>

        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="relative w-full h-full overflow-hidden shadow-2xl rounded-2xl bg-white/10 backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_four}
              alt="otp-verification"
              className="object-cover w-full h-full transition-transform duration-700 rounded-2xl hover:scale-110"
            />
            
            <motion.div 
              className="absolute inset-0 flex flex-col items-center justify-end pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="px-8 text-center">
                <motion.h2 
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Verify Your Business
                </motion.h2>
                <motion.p 
                  className="max-w-md mx-auto mb-8 text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Complete your vendor registration with secure verification
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div 
        className="w-full px-8 pt-16 pb-8 overflow-y-auto sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

  

        <motion.div
          className="mb-8 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Vendor Verification
          </h1>
          <p className="text-[#808080] text-base">
            We sent a verification code to your email. Enter the code to activate your vendor account.
          </p>
        </motion.div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const otp = `${values.otp_one}${values.otp_two}${values.otp_three}${values.otp_four}`;
            console.log("Submitted OTP:", otp);
          }}
        >
          {({ values, setFieldValue, isValid }) => (
            <Form className="w-full">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="w-full"
              >
                <div className="flex items-center justify-center gap-4 mb-8">
                  {["otp_one", "otp_two", "otp_three", "otp_four"].map(
                    (field, index) => (
                      <motion.div
                        key={field}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                        className="w-16 h-16 sm:w-20 sm:h-20"
                      >
                        <Field name={field}>
                          {({ field }) => (
                            <input
                              {...field}
                              ref={inputRefs[index]}
                              type="text"
                              inputMode="numeric"
                              maxLength="1"
                              autoComplete="off"
                              value={values[field.name] || ""}
                              onKeyUp={(e) => handleKeyUp(e, index, setFieldValue)}
                              className="w-full h-full text-3xl font-bold text-center rounded-2xl border-2 
                                focus:outline-none focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] 
                                transition-all duration-300 hover:border-[#FF6C6C]/50"
                              style={{
                                borderColor: values[field.name] !== "" 
                                  ? "#FF6C6C" 
                                  : field.name === document.activeElement?.name 
                                    ? "#FF6C6C" 
                                    : "#E5E7EB",
                                backgroundColor: values[field.name] !== "" 
                                  ? "#FFF5F5" 
                                  : "white",
                                color: values[field.name] !== "" 
                                  ? "#FF6C6C" 
                                  : "#374151",
                                boxShadow: values[field.name] !== "" 
                                  ? "0 4px 12px rgba(255,108,108,0.1)" 
                                  : field.name === document.activeElement?.name 
                                    ? "0 0 0 4px rgba(255,108,108,0.1)" 
                                    : "none",
                                transform: field.name === document.activeElement?.name 
                                  ? "translateY(-2px)" 
                                  : "translateY(0)",
                              }}
                            />
                          )}
                        </Field>
                      </motion.div>
                    )
                  )}
                </div>

                <motion.div 
                  className="flex justify-center mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <p className="text-[#808080]">
                    Didn't receive code?{" "}
                    <motion.button
                      type="button"
                      className="text-[#FF6C6C] font-semibold hover:text-[#ff8b8b]"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Resend
                    </motion.button>
                  </p>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={Object.values(values).some((val) => !val)}
                  className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                    Object.values(values).some((val) => !val)
                      ? "bg-[#ff6c6c9a] text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white shadow-lg hover:shadow-xl"
                  }`}
                  whileHover={!Object.values(values).some((val) => !val) ? { scale: 1.02 } : {}}
                  whileTap={!Object.values(values).some((val) => !val) ? { scale: 0.98 } : {}}
                >
                  Verify OTP
                </motion.button>
              </motion.div>
            </Form>
          )}
        </Formik>

        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Business Verified</div>
            </motion.div>
          </div>
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📱</div>
              <div className="text-xs text-gray-600">Instant Access</div>
            </motion.div>
          </div>
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🚀</div>
              <div className="text-xs text-gray-600">Start Selling</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default VerifyOtp;
